import React from 'react';
import { motion } from 'framer-motion';
import { SendIcon, InboxIcon, ShieldCheckIcon, DatabaseIcon, RefreshCwIcon } from 'lucide-react';
import { Container } from '../components/Container';
import { SectionHeader } from '../components/SectionHeader';
const steps = [{
  icon: SendIcon,
  label: 'Trigger',
  headline: 'Triggered by your systems',
  line: 'Policy bound, audit due, recs issued. Your systems kick off the chase.'
}, {
  icon: InboxIcon,
  label: 'Collect',
  headline: 'Producers reply with documents',
  line: 'Attachments or e-sign. Pigeon AI ingests and files everything against the checklist.'
}, {
  icon: ShieldCheckIcon,
  label: 'Verify',
  headline: 'AI validates every page',
  line: 'Custom rules enforce underwriting and compliance before a human ever touches it.'
}, {
  icon: DatabaseIcon,
  label: 'File',
  headline: 'Synced back to your systems',
  line: 'Documents, data, and audit trails flow into your DMS, policy admin, and reporting.'
}];
export function AutomatedLoop() {
  return <section className="relative overflow-hidden border-y border-white/5 bg-zinc-900/30 py-14 sm:py-28" data-id="element-524">
      <div className="pointer-events-none absolute inset-0 dot-grid opacity-30 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" data-id="element-525" />
      <Container className="relative" data-id="element-526">
        <SectionHeader align="center" eyebrow="The platform" title={<>
              Pigeon automates your workflows
              <br data-id="element-528" />
              <span className="italic text-accent" data-id="element-529">end to end.</span>
            </>} subtitle="One continuous loop that runs every follow-up and paper chase from start to finish, without a person in the middle." data-id="element-527" />

        {/* ===== MOBILE — horizontal timeline rows. Icon column on the left
             with a connecting vertical accent line; copy fills the rest of the
             width. Eliminates the empty top-right real estate. ===== */}
        <ol className="relative mt-10 lg:hidden" data-id="element-530">
          {/* Vertical connector through icon column */}
          <span aria-hidden className="pointer-events-none absolute left-6 top-6 bottom-6 w-px bg-gradient-to-b from-transparent via-accent/30 to-transparent" data-id="element-531" />
          {steps.map((s, i) => <motion.li key={s.label} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '400px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.06,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative grid grid-cols-[3rem_1fr] items-start gap-4 py-3 first:pt-0 last:pb-0" data-id="element-532">
              {/* Icon */}
              <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.08] bg-gradient-to-br from-accent/[0.16] to-accent/[0.04] shadow-[0_8px_24px_-12px_rgba(56,189,248,0.4)]" data-id="element-533">
                <s.icon className="h-5 w-5 text-accent" data-id="element-534" />
              </div>
              {/* Content */}
              <div className="surface relative overflow-hidden p-4" data-id="element-535">
                <div className="flex items-center gap-2" data-id="element-536">
                  <span className="eyebrow text-zinc-500" style={{
                fontSize: 10
              }} data-id="element-537">
                    {`0${i + 1}`}
                  </span>
                  <span className="h-px w-3 hairline" data-id="element-538" />
                  <span className="eyebrow text-accent" style={{
                fontSize: 10
              }} data-id="element-539">
                    {s.label}
                  </span>
                </div>
                <h3 className="mt-1.5 text-[17px] font-semibold leading-tight tracking-tight text-white" data-id="element-540">
                  {s.headline}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-zinc-400" data-id="element-541">
                  {s.line}
                </p>
              </div>
            </motion.li>)}
        </ol>

        {/* ===== DESKTOP — original card grid (unchanged) ===== */}
        <div className="relative mt-16 hidden lg:block" data-id="element-542">
          <div aria-hidden className="pointer-events-none absolute left-[10%] right-[10%] top-[3.25rem] h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" data-id="element-543" />
          <ol className="grid gap-4 lg:grid-cols-4 lg:gap-5" data-id="element-544">
            {steps.map((s, i) => <motion.li key={s.label} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '400px'
          }} transition={{
            duration: 0.55,
            delay: i * 0.07,
            ease: [0.16, 1, 0.3, 1]
          }} className="relative" data-id="element-545">
                <div className="surface relative h-full overflow-hidden p-6 sm:p-7" data-id="element-546">
                  <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full bg-accent/[0.05] blur-2xl" data-id="element-547" />
                  <div className="relative" data-id="element-548">
                    <div className="relative" data-id="element-549">
                      <div className="absolute inset-0 -z-10 rounded-2xl bg-accent/[0.08] blur-md" data-id="element-550" />
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.08] bg-gradient-to-br from-accent/[0.12] to-accent/[0.03]" data-id="element-551">
                        <s.icon className="h-5 w-5 text-accent" data-id="element-552" />
                      </div>
                    </div>
                    <div className="mt-6 flex items-center gap-2" data-id="element-553">
                      <span className="eyebrow text-zinc-500" style={{
                    fontSize: 10
                  }} data-id="element-554">
                        {`0${i + 1}`}
                      </span>
                      <span className="h-px flex-1 hairline" data-id="element-555" />
                      <span className="eyebrow text-accent" style={{
                    fontSize: 10
                  }} data-id="element-556">
                        {s.label}
                      </span>
                    </div>
                    <h3 className="mt-4 text-lg font-semibold leading-tight tracking-tight text-white sm:text-xl" data-id="element-557">
                      {s.headline}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-400" data-id="element-558">
                      {s.line}
                    </p>
                  </div>
                </div>
              </motion.li>)}
          </ol>
        </div>
      </Container>
    </section>;
}